import { useEffect, useRef, useState } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  ArrowLeft,
  ArrowRight,
  BrainCircuit,
  ChevronRight,
  History,
  RefreshCw,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react';
import { TooltipButton } from './TooltipButton';
import {
  historyWhen,
  type AnswerGroup,
  type HistoryGroup,
  type TrainingGroup,
} from './learning-history-groups';

/**
 * Teaching history in a side panel over Learning: sittings of operator answers
 * and training runs, newest first. Each entry opens its own detail in place;
 * opening a document is the only way out.
 */
export function LearningHistoryPanel({
  groups,
  loading,
  failed,
  trainingUnavailable = false,
  olderAnswersHidden = false,
  documentsUnavailable,
  onRetry,
  onClose,
  onOpenDocument,
}: {
  groups: readonly HistoryGroup[];
  loading: boolean;
  /** Your answers couldn't be read. */
  failed: boolean;
  /** Training runs couldn't be read: answers still show, but the list is not the whole story. */
  trainingUnavailable?: boolean;
  /** The read stopped at its limit, so the oldest answers are left out. */
  olderAnswersHidden?: boolean;
  documentsUnavailable: boolean;
  onRetry: () => void;
  onClose: () => void;
  onOpenDocument?: (documentId: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const body = useRef<HTMLDivElement>(null);
  const back = useRef<HTMLButtonElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const returnTo = useRef<string | null>(null);
  const open = groups.find((group) => group.id === openId) ?? null;
  // Opening or leaving an entry replaces the focused control; keep focus in the panel.
  useEffect(() => {
    body.current?.scrollTo({ top: 0 });
    setShowAll(false);
    if (openId) back.current?.focus();
    else if (returnTo.current)
      body.current
        ?.querySelector<HTMLButtonElement>(`[data-history-entry="${CSS.escape(returnTo.current)}"]`)
        ?.focus();
  }, [openId]);
  useEffect(() => {
    if (openId && !open) setOpenId(null);
  }, [open, openId]);
  const latest = useRef({ open: Boolean(open), onClose });
  latest.current = { open: Boolean(open), onClose };
  // Escape steps back inside the panel before it closes the panel, never the window behind it.
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (latest.current.open) setOpenId(null);
      else latest.current.onClose();
    };
    document.addEventListener('keydown', escape, true);
    return () => document.removeEventListener('keydown', escape, true);
  }, []);
  const show = (id: string) => {
    returnTo.current = id;
    setOpenId(id);
  };
  return (
    <aside className="lj-history" role="dialog" aria-label="Teaching history">
      <header>
        {open ? (
          <button ref={back} type="button" className="lj-back" onClick={() => setOpenId(null)}>
            <ArrowLeft size={16} />
            Teaching history
          </button>
        ) : (
          <h3>
            <History size={18} aria-hidden="true" />
            Teaching history
          </h3>
        )}
        <span className="lj-history-tools">
          {!open && (
            <TooltipButton
              type="button"
              className="icon-button"
              onClick={onRetry}
              disabled={loading}
              aria-label="Refresh history"
              tooltip="Refresh"
            >
              <RefreshCw size={16} />
            </TooltipButton>
          )}
          <button
            ref={close}
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close teaching history"
            autoFocus
          >
            <X size={18} />
          </button>
        </span>
      </header>
      <div className="lj-history-body" ref={body}>
        {open ? (
          open.kind === 'answers' ? (
            <AnswerDetail
              group={open}
              showAll={showAll}
              onShowAll={() => setShowAll((value) => !value)}
              onOpenDocument={onOpenDocument}
            />
          ) : (
            <TrainingDetail group={open} />
          )
        ) : (
          <>
            <p className="lj-quiet">
              Your answers, grouped by sitting, and what training did with them. Open any entry for
              details.
            </p>
            {documentsUnavailable && !loading && !failed && (
              <p className="lj-quiet" role="status">
                Documents couldn’t be checked, so they can’t be opened from here right now.
              </p>
            )}
            {loading ? (
              <p role="status" className="lj-quiet">
                Reading history…
              </p>
            ) : failed ? (
              <div
                className="lj-history-empty"
                role="alert"
                data-telemetry="error:learning-load-failed"
              >
                <p>History couldn’t be read.</p>
                <button type="button" className="quiet-button small" onClick={onRetry}>
                  <RefreshCw size={15} />
                  Try again
                </button>
              </div>
            ) : (
              <>
                {/* A failed re-read keeps the runs read earlier on screen: say they may be out of date, not that they're missing. */}
                {trainingUnavailable && (
                  <div
                    className="lj-history-empty"
                    role="alert"
                    data-telemetry="error:learning-load-failed"
                  >
                    <p>
                      {groups.some((group) => group.kind === 'training')
                        ? 'Training runs couldn’t be refreshed. The ones shown were read earlier.'
                        : 'Training runs couldn’t be read, so they aren’t listed here.'}
                    </p>
                    <button type="button" className="quiet-button small" onClick={onRetry}>
                      <RefreshCw size={15} />
                      Try again
                    </button>
                  </div>
                )}
                {!groups.length ? (
                  !trainingUnavailable && (
                    <div className="lj-history-empty">
                      <History size={28} aria-hidden="true" />
                      <p>No teaching yet. Confirm or correct a document and it shows up here.</p>
                    </div>
                  )
                ) : (
                  <ol className="lj-timeline">
                    {groups.map((group) => (
                      <li key={group.id}>
                        <button
                          type="button"
                          className={`lj-timeline-entry is-${group.kind}`}
                          data-history-entry={group.id}
                          onClick={() => show(group.id)}
                        >
                          <span className="lj-timeline-icon">
                            {group.kind === 'training' ? (
                              <BrainCircuit size={15} aria-hidden="true" />
                            ) : (
                              <UserRound size={15} aria-hidden="true" />
                            )}
                          </span>
                          <span className="lj-timeline-copy">
                            <span className="lj-timeline-title">
                              <strong>{group.title}</strong>
                              <time dateTime={group.at}>{historyWhen(group.at)}</time>
                            </span>
                            <small>{group.summary}</small>
                          </span>
                          <ChevronRight size={15} aria-hidden="true" className="lj-timeline-go" />
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
                {olderAnswersHidden && <p className="lj-quiet">Older answers aren’t shown here.</p>}
              </>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

function DetailHead({ group }: { group: HistoryGroup }) {
  return (
    <div className="lj-detail-head">
      <span className={`lj-timeline-icon is-${group.kind}`}>
        {group.kind === 'training' ? (
          <BrainCircuit size={16} aria-hidden="true" />
        ) : (
          <UserRound size={16} aria-hidden="true" />
        )}
      </span>
      <div>
        <h4>{group.title}</h4>
        <time dateTime={group.at}>{historyWhen(group.at)}</time>
      </div>
    </div>
  );
}

const DOCUMENT_LIMIT = 6;
function AnswerDetail({
  group,
  showAll,
  onShowAll,
  onOpenDocument,
}: {
  group: AnswerGroup;
  showAll: boolean;
  onShowAll: () => void;
  onOpenDocument?: (documentId: string) => void;
}) {
  const single = group.single;
  const byType = new Map<string, typeof group.documents>();
  for (const row of group.documents) {
    const key = row.typeName ?? 'Other documents';
    byType.set(key, [...(byType.get(key) ?? []), row]);
  }
  let shown = 0;
  return (
    <div className="lj-detail">
      <DetailHead group={group} />
      {group.partial && (
        <p className="lj-quiet">
          Earlier answers in this sitting aren’t shown, so these counts are a floor.
        </p>
      )}
      {single ? (
        <>
          <DocumentRow
            row={group.documents[0]}
            onOpenDocument={onOpenDocument}
            note={single.fieldName}
          />
          <div className="lj-change">
            <div>
              <small>It read</small>
              <s>{single.before ?? 'Blank'}</s>
            </div>
            <ArrowRight size={17} aria-hidden="true" />
            <div>
              <small>You set</small>
              <b>{single.after ?? 'Blank'}</b>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="lj-chips">
            {group.typeCounts.map((entry) => (
              <span key={entry.name}>
                {entry.name} · {entry.count}
              </span>
            ))}
            {group.fixes > 0 && (
              <span>
                {group.fixes} {group.fixes === 1 ? 'value fixed' : 'values fixed'}
              </span>
            )}
            {group.checks > 0 && (
              <span>
                {group.checks} {group.checks === 1 ? 'value checked' : 'values checked'}
              </span>
            )}
            {group.reopenedDocuments > 0 && <span>{group.reopenedDocuments} reopened</span>}
          </div>
          <div className="lj-detail-section">
            <span className="lj-eyebrow">Documents · click one to open it</span>
            {[...byType].map(([name, rows]) => (
              <div key={name} className="lj-doc-group">
                <div className="lj-doc-group-head">
                  <span>{name}</span>
                  <span>{rows.length}</span>
                </div>
                {rows
                  .filter(() => showAll || shown++ < DOCUMENT_LIMIT)
                  .map((row) => (
                    <DocumentRow key={row.id} row={row} onOpenDocument={onOpenDocument} />
                  ))}
              </div>
            ))}
            {group.documents.length > DOCUMENT_LIMIT && (
              <button type="button" className="lj-link" onClick={onShowAll}>
                {showAll ? 'Show fewer' : `Show ${group.documents.length - DOCUMENT_LIMIT} more`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DocumentRow({
  row,
  note,
  onOpenDocument,
}: {
  row: AnswerGroup['documents'][number];
  note?: string;
  onOpenDocument?: (documentId: string) => void;
}) {
  const state = [
    row.confirmed ? 'Confirmed' : row.reopened ? 'Reopened' : null,
    row.checks ? `${row.checks} checked` : null,
    row.fixes ? `${row.fixes} fixed` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const openable = row.available && Boolean(onOpenDocument);
  return (
    <button
      type="button"
      className="lj-doc"
      disabled={!openable}
      onClick={() => onOpenDocument?.(row.id)}
      title={openable ? undefined : 'No longer in this job'}
    >
      <span className="lj-doc-page" aria-hidden="true" />
      <span>
        <b>{row.label}</b>
        <small>{[row.typeName, note].filter(Boolean).join(' · ') || 'Document'}</small>
      </span>
      <em className={row.fixes ? 'has-fixes' : ''}>{state}</em>
      {openable && <ChevronRight size={14} aria-hidden="true" />}
    </button>
  );
}

function TrainingDetail({ group }: { group: TrainingGroup }) {
  const session = group.session;
  const minutes = session.endedAt
    ? Math.max(
        0,
        Math.round((Date.parse(session.endedAt) - Date.parse(session.startedAt)) / 60_000),
      )
    : null;
  const duration = group.closing
    ? group.closing
    : minutes === null || !Number.isFinite(minutes)
      ? 'Still running'
      : minutes >= 60
        ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
        : `${minutes}m`;
  return (
    <div className="lj-detail">
      <DetailHead group={group} />
      <div className="lj-stats">
        <div>
          <b>{session.tried.toLocaleString()}</b>
          <small>tried</small>
        </div>
        <div>
          <b className="is-good">{group.kept.length.toLocaleString()}</b>
          <small>kept</small>
        </div>
        <div>
          <b>{session.refused.toLocaleString()}</b>
          <small>left as is</small>
        </div>
        <div>
          <b>{duration}</b>
          <small>{session.mode === 'scheduled' ? 'scheduled' : 'started by you'}</small>
        </div>
      </div>
      {/* A run still going has saved nothing yet; that is not results lost. */}
      {!session.lastCheckpoint && (
        <p className="lj-quiet">
          {!group.closing &&
          (session.state === 'running' ||
            session.state === 'stopping' ||
            session.state === 'resting')
            ? 'No results saved yet · still running'
            : 'Detailed results were not saved for this run.'}
        </p>
      )}
      {group.kept.length > 0 && (
        <div className="lj-detail-section">
          <span className="lj-eyebrow">Kept</span>
          <ul className="lj-kept">
            {group.kept.map((line, index) => (
              <li key={index}>
                <ShieldCheck size={17} aria-hidden="true" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {group.unchanged.length > 0 && (
        <div className="lj-detail-section">
          <span className="lj-eyebrow">Left as is, and why</span>
          <ul className="lj-unchanged">
            {group.unchanged.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {session.operationalFailures > 0 && (
        <p className="lj-quiet">
          {session.operationalFailures.toLocaleString()}{' '}
          {session.operationalFailures === 1 ? 'check' : 'checks'} had issues and were not counted.
        </p>
      )}
    </div>
  );
}
